/**
 * 支付页的展示规则（纯函数，可测）。
 *
 * 为什么规则要放在这里而不是 SFC 里：本仓库的 vitest 只跑 `src` 下的 `*.test.ts`，
 * 不编译 `.vue`。把「价格和额度从哪来」「拒绝升级该怎么说」留在组件里，
 * 就等于这些规则永远不会被回归测试覆盖——而它们恰好是最容易算错的部分。
 *
 * 两条不可动摇的约束：
 * 1. 金额与额度**只从契约推导**（`PLAN_PRICES` / `QUOTA_TIER_MULTIPLIERS`），一个字都不硬编码。
 *    前端显示一个价、后端收另一个价，是支付功能里最贵的一类 bug。
 * 2. 时间一律显式传参。这里不读 `Date.now()`，否则「上一秒 / 下一秒」的用例
 *    会变成看运气的测试，而倒计时正是这类边界的重灾区。
 *
 * v1.3.0：额度改成只说"相对 Lite 的倍率"，绝对 token 量不再进前端 bundle。
 */

import {
  formatPriceYuan,
  planLabelOf,
  planPriceOf,
  quotaTierLabelOf,
  PAID_PLAN_IDS,
  type BillingCycle,
  type Order,
  type OrderKind,
  type PaidPlanId,
  type PaymentChannel,
  type PlanChangeResult,
} from '@spirit/contracts';

/** 渠道选项。`id` 直接用契约的 `PaymentChannel`，改契约时这里编译不过。 */
export interface ChannelOption {
  id: PaymentChannel;
  label: string;
  hint: string;
}

export const CHANNEL_OPTIONS: readonly ChannelOption[] = [
  { id: 'wechat', label: '微信支付', hint: '生成二维码后用微信扫码' },
  { id: 'alipay', label: '支付宝', hint: '跳转到支付宝网页收银台' },
];

/** 周期选项。 */
export interface CycleOption {
  id: BillingCycle;
  label: string;
  hint: string;
}

export const CYCLE_OPTIONS: readonly CycleOption[] = [
  { id: 'monthly', label: '月付', hint: '按月续费' },
  { id: 'yearly', label: '年付', hint: '省 2 个月' },
];

/** 默认选中的档位。设计文档 §2.1 把 Pro 作为主推档。 */
export const DEFAULT_CHECKOUT_PLAN: PaidPlanId = 'pro';

/** 一张套餐卡片需要的全部展示数据。 */
export interface PlanCard {
  planId: PaidPlanId;
  label: string;
  /** 额度说明（相对 Lite 的倍率，见 `quotaTierLabelOf()`），不含任何绝对 token 量。 */
  quotaTierLabel: string;
  amountFen: number;
  originalAmountFen: number | null;
  recommended: boolean;
}

/**
 * 按周期列出可购买的套餐卡片。
 *
 * 额度与价格全部来自契约常量；`originalAmountFen` 只在年付时给出
 * （= 月付价 × 12），用来画划线价，让「省 2 个月」有一个可验证的数字，
 * 而不是一句拍脑袋的营销文案。
 */
export function planCardsOf(cycle: BillingCycle): PlanCard[] {
  return PAID_PLAN_IDS.map((planId) => ({
    planId,
    label: planLabelOf(planId),
    quotaTierLabel: quotaTierLabelOf(planId),
    amountFen: planPriceOf(planId, cycle),
    originalAmountFen: cycle === 'yearly' ? planPriceOf(planId, 'monthly') * 12 : null,
    recommended: planId === DEFAULT_CHECKOUT_PLAN,
  }));
}

/** 结算摘要里的一段人话 + 供确认弹窗复用的金额。 */
export interface PlanChangeCopy {
  kind: OrderKind;
  amountFen: number;
  headline: string;
  detail: string;
}

/**
 * 把 `quotePlanChange` 的成功结果翻译成人话。
 *
 * 入参用 `Extract<..., { ok: true }>` 而不是 `PlanChangeResult`：让"把拒绝当变更
 * 格式化"在类型层就不成立，调用方必须先判 `ok`。
 */
export function describePlanChange(
  result: Extract<PlanChangeResult, { ok: true }>,
  cycle: BillingCycle,
): PlanChangeCopy {
  const quote = result.quote;
  const label = planLabelOf(quote.planId);
  const money = `¥${formatPriceYuan(quote.amountFen)}`;

  switch (quote.kind) {
    case 'purchase':
      return {
        kind: 'purchase',
        amountFen: quote.amountFen,
        headline: `开通 ${label}`,
        detail: `${cycleLabelOf(cycle)}付费 ${money}，权益立即生效，${cycleNounOf(cycle)}后到期并自动回到 ${planLabelOf('free')}。`,
      };
    case 'renewal':
      return {
        kind: 'renewal',
        amountFen: quote.amountFen,
        headline: `续费 ${label}`,
        detail: `${cycleLabelOf(cycle)}付费 ${money}，从当前到期时间起顺延${cycleNounOf(cycle)}；已付的剩余天数不会被覆盖。`,
      };
    case 'upgrade':
      return {
        kind: 'upgrade',
        amountFen: quote.amountFen,
        headline: `升级到 ${label}`,
        detail: `补差价 ${money}，本期到期时间不变。`,
      };
  }
}

/**
 * 把 `quotePlanChange` 的拒绝理由翻译成人话。
 *
 * 拒绝不是错误：降级本来就不允许，用户需要知道"为什么不行、什么时候行"，
 * 而不是看到一句「操作失败」。
 */
export function describePlanChangeRefusal(
  result: Extract<PlanChangeResult, { ok: false }>,
  cycle: BillingCycle,
): string {
  const current = planLabelOf(result.currentPlanId);
  switch (result.reason) {
    case 'downgrade-not-allowed':
      return `当前是 ${current}，本期内不支持降级；到期后可自由选择套餐。`;
    case 'cycle-mismatch':
      return `当前订阅是${cycleLabelOf(result.currentBillingCycle)}付费，升级必须保持同一周期；想改成${cycleLabelOf(cycle)}付费，请在到期后重新购买。`;
  }
}

/** 倒计时状态。 */
export interface PaymentCountdown {
  expired: boolean;
  text: string;
}

/**
 * 订单支付倒计时（`mm:ss`）。
 *
 * 解析失败按「已过期」处理：时间戳是我们刚从下单响应里拿到的，解析不出来只能是
 * 数据坏了；此时显示一个永不前进的 `15:00` 会让用户一直等一笔已经死掉的订单。
 */
export function paymentCountdown(payExpiresAt: string, now: number): PaymentCountdown {
  const expiresAt = Date.parse(payExpiresAt);
  if (Number.isNaN(expiresAt)) return { expired: true, text: '00:00' };

  const remainingMs = expiresAt - now;
  // 用 `<= 0` 而不是 `< 0`：恰好归零的那一秒就该算过期。
  if (remainingMs <= 0) return { expired: true, text: '00:00' };

  const totalSeconds = Math.ceil(remainingMs / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return {
    expired: false,
    text: `${pad2(minutes)}:${pad2(seconds)}`,
  };
}

/**
 * 还能付款的那笔订单（不能付款时返回 null）。
 *
 * 「待支付」不等于"手里有订单对象"，而是 `status === 'pending'`。这个区分是必需的：
 * 支付成功后 `order` 里放的仍是刚拿到的那一笔（此时 `status === 'paid'`）。如果渲染
 * 条件只判 `order !== null`，那张「有一笔待支付订单」的卡片会带着已支付的订单和它的
 * 15 分钟倒计时一起回到页面上——用户会以为钱没付成功，而权益其实已经到账了。
 *
 * 契约 §3.5：`closed` 与 `failed` 都是"不可支付"，同样不返回（`failed` 目前没有生产者，
 * 但客户端必须按"不可支付"处置，不能只认 `closed`）。
 */
export function payableOrderOf(order: Order | null): Order | null {
  return order !== null && order.status === 'pending' ? order : null;
}

const IDEMPOTENCY_KEY_PREFIX = 'idem_';
const UUID_HEX_LENGTH = 32;

/**
 * 生成下单用的幂等键（契约要求 8-128 字符）。
 *
 * `crypto.randomUUID()` 只在安全上下文（https / localhost）可用：用局域网 IP
 * 打开 Vite dev server 时它是 `undefined`，于是「点支付」会直接抛 TypeError。
 * 退回 `crypto.getRandomValues()` 仍然是同一量级的随机性，且长度形状一致。
 * 两种情况都落在 `IdempotencyKeySchema` 的 8-128 区间内（本实现恒为 37）。
 */
export function newIdempotencyKey(): string {
  const cryptoApi = globalThis.crypto;
  const hex =
    typeof cryptoApi?.randomUUID === 'function'
      ? cryptoApi.randomUUID().replace(/-/g, '')
      : randomHex(UUID_HEX_LENGTH);
  return `${IDEMPOTENCY_KEY_PREFIX}${hex}`;
}

function randomHex(length: number): string {
  const bytes = new Uint8Array(Math.ceil(length / 2));
  const cryptoApi = globalThis.crypto;
  if (typeof cryptoApi?.getRandomValues === 'function') {
    cryptoApi.getRandomValues(bytes);
  } else {
    for (let index = 0; index < bytes.length; index += 1) {
      bytes[index] = Math.floor(Math.random() * 256);
    }
  }
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0'))
    .join('')
    .slice(0, length);
}

function pad2(value: number): string {
  return String(value).padStart(2, '0');
}

function cycleLabelOf(cycle: BillingCycle): string {
  return cycle === 'monthly' ? '按月' : '按年';
}

function cycleNounOf(cycle: BillingCycle): string {
  return cycle === 'monthly' ? '一个月' : '一年';
}
